import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, QrCode, CheckCircle } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { sensorKitFormSchema } from '@/installFlow/schemas'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepHeader } from '@/components/installFlow/StepHeader'
import { StepFooter } from '@/components/installFlow/StepFooter'
import { useInstallFlowNavigation } from '@/installFlow/useInstallFlowNavigation'
import { useStepSearch } from '@/installFlow/useStepSearch'
import { useQuery } from '@tanstack/react-query'
import { sensorKits } from '@/sensorKits/api'

export function SensorScanStep() {
  const search = useStepSearch('sensor-scan')
  const { goToStep } = useInstallFlowNavigation()

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [serialNumberToValidate, setSerialNumberToValidate] = useState<string | null>(null)

  const form = useForm({
    resolver: zodResolver(sensorKitFormSchema),
    defaultValues: {
      sensor_kit_serial_number: search.installSensorKitSerialNumber || '',
    },
  })

  const { data: sensorKit, isLoading: isValidating, error: validationError } = useQuery({
    ...sensorKits.detail(serialNumberToValidate!),
    enabled: !!serialNumberToValidate,
  })

  // Handle validation result in useEffect to avoid setState during render
  useEffect(() => {
    if (!serialNumberToValidate) return

    // Handle successful validation
    if (sensorKit) {
      // Check if sensor kit is already installed
      if (sensorKit.current_installation) {
        const installationName = sensorKit.current_installation.name || 'an equipment unit'
        setErrorMessage(
          `This sensor kit is already installed on ${installationName}. ` +
          `Please uninstall it first or use a different sensor kit.`
        )
        setSerialNumberToValidate(null)
      } else {
        // Sensor is valid and not installed - proceed
        goToStep('intro', { installSensorKitSerialNumber: serialNumberToValidate })
      }
    }

    // Handle validation error
    if (validationError) {
      setErrorMessage('Sensor kit not found. Please check the serial number and try again.')
      setSerialNumberToValidate(null)
    }
  }, [sensorKit, validationError, serialNumberToValidate, goToStep])

  const handleSubmit = form.handleSubmit((data) => {
    setErrorMessage(null)
    setSerialNumberToValidate(data.sensor_kit_serial_number)
  })

  const isPreFilled = !!search.installSensorKitSerialNumber

  return (
    <StepFrame
      header={
        <StepHeader
          title="Scan Thalo Sensor QR Code"
        />
      }
      main={
        <div className="space-y-6">
          {isPreFilled && !errorMessage && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                ✓ Sensor kit serial number has been pre-filled
              </AlertDescription>
            </Alert>
          )}

          <Card className="p-6">
            <Form {...form}>
              <form onSubmit={handleSubmit} className="space-y-6">
                {!isPreFilled && (
                  <>
                    <div className="flex justify-center py-8">
                      <div className="text-center">
                        <QrCode className="h-24 w-24 mx-auto text-muted-foreground mb-4" />
                        <p className="text-sm text-muted-foreground">
                          Scan the QR code on your Thalo sensor kit
                        </p>
                      </div>
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">Or enter manually</span>
                      </div>
                    </div>
                  </>
                )}

                <FormField
                  control={form.control}
                  name="sensor_kit_serial_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sensor Kit Serial Number *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., RG123456"
                          autoFocus={!isPreFilled}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        The serial number is printed on the sensor kit label
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {errorMessage && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                )}
              </form>
            </Form>
          </Card>
        </div>
      }
      footer={
        <StepFooter
          actions={[
            <Button
              key="continue"
              onClick={handleSubmit}
              disabled={isValidating}
              variant="thalo"
              className="w-full"
              size="lg"
            >
              {isValidating ? 'Validating...' : 'Continue'}
            </Button>
          ]}
        />
      }
    />
  )
}
